import React, { useMemo, useRef, useState, useEffect } from "react";
import moment from "moment-timezone";
import { Calendar, CalendarCell } from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";
import { getUserTimeZone } from "../../utils/timeZone";

import CALENDAR_ICON from "../../../public/assets/button/icon_calendar.svg";
import LEFT_ICON from "../../../public/assets/button/arrow-chevron-left.svg";
import RIGHT_ICON from "../../../public/assets/button/arrow-chevron-right.svg";
import { loadMessages } from "@progress/kendo-react-intl";

export const calendarDateToUserMoment = (value) => {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        return moment(value);
    }

    return moment.tz(
        [value.getFullYear(), value.getMonth(), value.getDate()],
        getUserTimeZone(),
    );
};

export const userMomentToCalendarDate = (value) => {
    const userDate = moment(value);
    if (!userDate.isValid()) return new Date(NaN);

    // Kendo's Calendar reads the browser-local calendar day from Date values.
    // Use local noon to avoid DST transitions where local midnight is skipped.
    return new Date(userDate.year(), userDate.month(), userDate.date(), 12);
};

export default function DateSwitcher({
    filter,
    setFilter,
    step,
    setStep,
    selectedWeekDay,
    setSelectedWeekDay,
    position = "right",
    showOption = true
}) {
    const [showPopup, setShowPopup] = useState(false);
    const [tempFilter, setTempFilter] = useState(filter);
    const getRangeDate = (rangeFilter, rangeStep) => rangeFilter === 1
        ? moment().clone().add(rangeStep, "months")
        : moment().clone().add(rangeStep, "weeks");
    const [tempDate, setTempDate] = useState(filter === 3
        ? selectedWeekDay?.date ? moment(selectedWeekDay.date) : moment()
        : getRangeDate(filter, step));
    const anchorRef = useRef(null);
    const popupRef = useRef(null);

    const now = moment();
    const selectedDate = filter === 3
        ? selectedWeekDay?.date ? moment(selectedWeekDay.date) : now
        : getRangeDate(filter, step);

    const isTodaySelected = filter === 3 && selectedDate.isSame(now, "day");
    const isThisWeekSelected = filter === 2 && selectedDate.isSame(now, "week");
    const isThisMonthSelected = filter === 1 && selectedDate.isSame(now, "month");

    const formatWeekRange = (date) => {
        const start = moment(date).startOf("week");
        const end = moment(date).endOf("week");
        return `${start.format("MMM DD")} - ${end.format("MMM DD")}`;
    };

    const WeekCalendarCell = (props) => {
        const cellDate = calendarDateToUserMoment(props.value);
        const weekStart = tempDate.clone().startOf("week");
        const weekEnd = tempDate.clone().endOf("week");
        const isRangeStart = cellDate.isSame(weekStart, "day");
        const isRangeEnd = cellDate.isSame(weekEnd, "day");
        const isRangeMid = cellDate.isBetween(weekStart, weekEnd, "day", "()");

        return (
            <CalendarCell
                {...props}
                isSelected={false}
                isInRange={isRangeStart || isRangeMid || isRangeEnd}
                isRangeStart={isRangeStart}
                isRangeMid={isRangeMid}
                isRangeEnd={isRangeEnd}
            >
                {props.formattedValue}
            </CalendarCell>
        );
    };

    const createSelectedWeekDay = (date) => {
        const m = moment(date);
        return {
            key: m.format("ddd"),                // 星期缩写
             date: m, // ✅ 改这里：统一为 moment
            mappingDate: m.format("YYYY-MM-DD"), // 映射日期
            format: m.format("YYYY/MM/DD"),      // 用于匹配
            color: "#CA2921",                    // 默认颜色，可根据业务调整
            released_seconds: 0,                 // 默认值
            background: "#FCEFEE",               // 默认背景
        };
    };

    const getWeekStep = (date) => {
        const selected = moment(date);
        const startOfSelectedWeek = selected.clone().startOf("week"); // 周一或周日视locale
        const startOfCurrentWeek = moment().startOf("week");

        return startOfSelectedWeek.diff(startOfCurrentWeek, "weeks");
    };
    const handleDayChange = (date) => {
        const m = moment(date);
        setSelectedWeekDay(createSelectedWeekDay(m));
        const weekStep = getWeekStep(m);
        setStep(weekStep); // 按周偏移 step
    };

    // 点击左右箭头
    const handlePrev = () => {
        if (filter === 3) {
            const newDate = selectedDate.clone().subtract(1, "day");
            handleDayChange(newDate);
        } else if (filter === 2) {
            setStep(s => s - 1);
        } else {
            setStep(s => s - 1);
        }
    };

    const handleNext = () => {
        if (filter === 3) {
            const newDate = selectedDate.clone().add(1, "day");
            handleDayChange(newDate);
        } else if (filter === 2) {
            setStep(s => s + 1);
        } else {
            setStep(s => s + 1);
        }
    };

    // Today 按钮
    const handleTodayOrCurrentRange = () => {
        if (filter === 3) handleDayChange(moment());
        else setStep(0);
    };

    const applySelection = (selectedDate, selectedFilter) => {
        const mDate = moment(selectedDate);

        if (selectedFilter === 3) {
            handleDayChange(mDate);
        } else if (selectedFilter === 2) {
            setStep(getWeekStep(mDate));
        } else {
            setStep(mDate.clone().startOf("month").diff(moment().startOf("month"), "months"));
        }

        setFilter(selectedFilter);
        setShowPopup(false);
    };

    // 选择日历时（实时更新 temp + 应用）
    const handleCalendarSelect = (date) => {
        const selectedDate = calendarDateToUserMoment(date);
        setTempDate(selectedDate);
        applySelection(selectedDate, tempFilter);
    };

    // Popup 关闭时（用缓存的 temp 值）
    const handlePopupClose = () => {
        applySelection(tempDate, tempFilter);
    };

    // 点击外部关闭 Popup
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showPopup && popupRef.current && !popupRef.current.contains(e.target) && !anchorRef.current.contains(e.target)) {
                handlePopupClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showPopup, tempFilter, tempDate]);

    useEffect(() => {
        const interval = setInterval(() => {
            const todayBtn = document.querySelector(".k-calendar-nav-today .k-button-text");
            if (todayBtn) {
            todayBtn.textContent = tempFilter === 3
                ? "Today"
                : tempFilter === 2 ? "This Week" : "This Month";
            }
        }, 100); // 每100ms检查一次按钮

        return () => clearInterval(interval);
    }, [tempFilter]);

    // 打开 Popup 初始化临时状态
    const handleOpenPopup = () => {
        setTempFilter(filter);
        setTempDate(selectedDate);
        setShowPopup(true);
    };

    return (
        <div className="ds-container">

            {((filter === 3 && isTodaySelected) || (filter === 2 && isThisWeekSelected) || (filter === 1 && isThisMonthSelected)) && (
                <div ref={anchorRef} className="ds-main-button"  onClick={handleOpenPopup}> 
                <img src={CALENDAR_ICON} /> 
                { filter === 3 && <span style={{fontSize:'18px'}}>{selectedDate.format("ddd")}</span> } 
                <span style={{ fontWeight: 600 ,fontSize:'18px'}}>{filter === 3 ? selectedDate.format("MM/DD") : filter === 2 ? formatWeekRange(selectedDate) : selectedDate.format("MMM YYYY")}</span>
                </div> 
            )}

            {((filter === 3 && !isTodaySelected) || (filter === 2 && !isThisWeekSelected) || (filter === 1 && !isThisMonthSelected)) &&
                (<div className="ds-button-container">
                    <div ref={anchorRef} className="ds-button-left" onClick={handleOpenPopup}>
                        <img src={CALENDAR_ICON} />
                        {filter === 3 && <span style={{fontSize:'18px'}}>{selectedDate.format("ddd")}</span>}
                        <span style={{ fontWeight: 600 ,fontSize:'18px'}}>{filter === 3 ? selectedDate.format("MM/DD") : filter === 2 ? formatWeekRange(selectedDate) : selectedDate.format("MMM YYYY")}</span>
                    </div> 
                    {(filter === 3 && !isTodaySelected) && (<button className="ds-return" onClick={handleTodayOrCurrentRange}>Today</button>)}
                    {(filter === 2 && !isThisWeekSelected) && (<button className="ds-return" onClick={handleTodayOrCurrentRange}>This Week</button>)}
                    {(filter === 1 && !isThisMonthSelected) && (<button className="ds-return" onClick={handleTodayOrCurrentRange}>This Month</button>)}
                </div>
            )}


            <button className="btn-image" onClick={handlePrev}>
                <img src={LEFT_ICON} />
            </button>

            <button className="btn-image" onClick={handleNext}>
                <img src={RIGHT_ICON} />
            </button>

            <Popup popupClass="ds-filter-popup" anchor={anchorRef.current} show={showPopup}
                    anchorAlign={{ horizontal: position, vertical: "bottom" }}
                    popupAlign={{ horizontal: position, vertical: "top" }}>
                <div ref={popupRef}>
                    {
                        showOption &&
                        <div className="ds-popup-mode">
                            <button
                                className={tempFilter === 3 ? "active" : ""}
                                onClick={() => setTempFilter(3)}
                            >
                                Day
                            </button>
                            <button
                                className={tempFilter === 2 ? "active" : ""}
                                onClick={() => setTempFilter(2)}
                            >
                                Week
                            </button>
                            <button
                                className={tempFilter === 1 ? "active" : ""}
                                onClick={() => setTempFilter(1)}
                            >
                                Month
                            </button>
                        </div>
                    }

                    {
                        tempFilter === 2 &&
                        <Calendar
                            value={userMomentToCalendarDate(tempDate)}
                            onChange={(e) => handleCalendarSelect(e.value)}
                            cell={WeekCalendarCell}
                        />
                    }

                    {
                        tempFilter === 1 &&
                        <Calendar
                            value={userMomentToCalendarDate(tempDate)}
                            onChange={(e) => handleCalendarSelect(e.value)}
                            bottomView={'year'}
                            topView={'decade'}
                        />
                    }

                    {
                        tempFilter === 3 &&
                        <Calendar
                            value={userMomentToCalendarDate(tempDate)}
                            onChange={(e) => handleCalendarSelect(e.value)}
                        />
                    }

                </div>
            </Popup>

            <style>{`
                .ds-container { display: flex; align-items: center; gap: 8px; }
                .ds-main-button { display: flex; height: 40px; justify-content: center; align-items: center; gap: 8px; padding: 0 12px 0 16px; border-radius: 40px; border: 1px solid #D9D9D9; cursor: pointer; box-sizing: border-box; }
                .ds-main-button:hover { background: #D9D9D9; }
                .ds-filter-popup { display: inline-flex; min-width: 120px; padding: 16px; flex-direction: column; align-items: flex-start; gap: 12px; border-radius: 4px; background: #FFF; box-shadow: 0 1.973px 9.867px rgba(0,0,0,0.2); }
                .ds-popup-mode { display: flex; gap: 8px; margin-bottom: 12px; }
                .ds-popup-mode button { background: #fff; display: flex; height: 30px; padding: 0 12px; justify-content: center; align-items: center; gap: 4px; border-radius: 40px; border: 1px solid #D9D9D9; color: #333; cursor: pointer; }
                .ds-popup-mode button.active { background: #865bdf; color: #fff; border-color: #865bdf; }
                .ds-button-container { display: flex; background: #fff; }  
                .ds-button-left { display: flex; height: 40px; justify-content: center; align-items: center; gap: 8px; padding: 0 12px 0 16px; border-radius: 40px 0 0 40px; border: 1px solid #D9D9D9; }
                .ds-button-left:hover { background: #D9D9D9;}
                .ds-return { display: flex; padding: 0 16px 0 12px; justify-content: center; align-items: center; gap: 4px; align-self: stretch; border-radius: 0 40px 40px 0; border-top: 1px solid #D9D9D9; border-right: 1px solid #D9D9D9; border-bottom: 1px solid #D9D9D9; border-left: none; background: #fff }
                .ds-return:hover { background: #D9D9D9;}
            `}</style>
        </div>
    );
}
